import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Modal, Button, Input, Select, Textarea, Spinner
} from '../../components/UI';
import { smartListsAPI, leadsAPI, pipelineAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { HiOutlineSparkles } from 'react-icons/hi';

const EMPTY_FILTERS = {
  city: '',
  source: '',
  status: '',
  stage: '',
  priority: '',
  minLeadScore: '',
  maxLeadScore: '',
  tags: '',
  assignment: '',
  lastContactedWithinDays: '',
  neverContacted: false,
  nextFollowUpWithinDays: '',
  createdAfter: '',
  createdBefore: '',
};

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'cold', label: 'Cold' },
  { value: 'warm', label: 'Warm' },
  { value: 'hot', label: 'Hot' },
  { value: 'converted', label: 'Converted' },
  { value: 'closed', label: 'Closed' },
  { value: 'lost', label: 'Lost' },
  { value: 'not_connected', label: 'Not Connected' },
];

const PRIORITY_OPTIONS = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const ASSIGNMENT_OPTIONS = [
  { value: 'assigned', label: 'Assigned to a telecaller' },
  { value: 'unassigned', label: 'Unassigned' },
];

const COLOR_OPTIONS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899', '#6B7280'];

const SmartListModal = ({ isOpen, onClose, smartList, onSuccess }) => {
  const queryClient = useQueryClient();
  const isEdit = Boolean(smartList?._id);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#3B82F6');
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [preview, setPreview] = useState(null);

  // Reset form state when the modal opens for a different smart list
  useEffect(() => {
    if (!isOpen) return;
    setPreview(null);
    if (smartList) {
      setName(smartList.name || '');
      setDescription(smartList.description || '');
      setColor(smartList.color || '#3B82F6');
      setFilters({ ...EMPTY_FILTERS, ...(smartList.filters || {}) });
    } else {
      setName('');
      setDescription('');
      setColor('#3B82F6');
      setFilters({ ...EMPTY_FILTERS });
    }
  }, [isOpen, smartList]);

  // Sources list for the dropdown (unique lead sources in this org)
  const { data: sources } = useQuery({
    queryKey: ['leadSources'],
    queryFn: async () => {
      const response = await leadsAPI.getSources();
      const list = response.data?.data || [];
      return list.map((s) => (typeof s === 'string' ? s : s.source || s.name)).filter(Boolean);
    },
    enabled: isOpen,
  });

  // Pipeline stages for the stage dropdown (dynamic per organization)
  const { data: stages } = useQuery({
    queryKey: ['pipelineStages'],
    queryFn: async () => {
      const response = await pipelineAPI.getStats({ range: 'all' });
      const stats = response.data?.data || [];
      return stats.map((s) => ({ key: s.key || s.value, name: s.name })).filter((s) => s.key);
    },
    enabled: isOpen,
  });

  const buildFiltersPayload = () => {
    const payload = {};
    if (filters.city.trim()) payload.city = filters.city.trim();
    if (filters.source.trim()) payload.source = filters.source.trim();
    if (filters.status) payload.status = filters.status;
    if (filters.stage) payload.stage = filters.stage;
    if (filters.priority) payload.priority = filters.priority;
    if (filters.minLeadScore !== '') payload.minLeadScore = Number(filters.minLeadScore);
    if (filters.maxLeadScore !== '') payload.maxLeadScore = Number(filters.maxLeadScore);
    const tags = filters.tags.split(',').map((t) => t.trim()).filter(Boolean);
    if (tags.length > 0) payload.tags = tags;
    if (filters.assignment) payload.assignment = filters.assignment;
    if (filters.lastContactedWithinDays !== '') payload.lastContactedWithinDays = Number(filters.lastContactedWithinDays);
    if (filters.neverContacted) payload.neverContacted = true;
    if (filters.nextFollowUpWithinDays !== '') payload.nextFollowUpWithinDays = Number(filters.nextFollowUpWithinDays);
    if (filters.createdAfter) payload.createdAfter = filters.createdAfter;
    if (filters.createdBefore) payload.createdBefore = filters.createdBefore;
    return payload;
  };

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPreview(null);
  };

  const previewMutation = useMutation({
    mutationFn: () => smartListsAPI.preview(buildFiltersPayload()),
    onSuccess: (response) => {
      const data = response.data?.data || {};
      setPreview({ count: data.count ?? 0, sample: data.sample || [] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to preview matches');
    },
  });

  const saveMutation = useMutation({
    mutationFn: (data) => (isEdit ? smartListsAPI.update(smartList._id, data) : smartListsAPI.create(data)),
    onSuccess: () => {
      queryClient.invalidateQueries(['smartLists']);
      toast.success(isEdit ? 'Smart list updated successfully' : 'Smart list created successfully');
      onSuccess?.();
      onClose();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to save smart list');
    },
  });

  const handleSave = () => {
    if (!name.trim()) {
      toast.error('Smart list name is required');
      return;
    }
    saveMutation.mutate({
      name: name.trim(),
      description,
      color,
      filters: buildFiltersPayload(),
    });
  };

  const activeFilterCount = Object.keys(buildFiltersPayload()).length;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? 'Edit Smart List' : 'Create Smart List'} size="lg">
      <div className="space-y-4">
        {/* Basic details */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Hot Dubai Leads"
          />
          <div className="space-y-1.5">
            <label className="block text-sm font-semibold text-secondary-700">Color</label>
            <div className="flex flex-wrap items-center gap-2 py-1.5">
              {COLOR_OPTIONS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`w-7 h-7 rounded-full border-2 transition-all ${color === c ? 'border-secondary-900 scale-110' : 'border-transparent'}`}
                  style={{ backgroundColor: c }}
                  title={c}
                />
              ))}
            </div>
          </div>
        </div>

        <Textarea
          label="Description"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What is this list used for? e.g. Weekly follow-up call list"
        />

        {/* Filter builder */}
        <div className="border border-secondary-200 rounded-xl p-4 space-y-4">
          <div className="flex items-center gap-2">
            <HiOutlineSparkles className="w-5 h-5 text-primary-600" />
            <h3 className="text-sm font-bold text-secondary-900">Filters</h3>
            <span className="text-xs text-secondary-400">
              (leave a filter empty to ignore it — matches update automatically as leads change)
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="City"
              type="text"
              value={filters.city}
              onChange={(e) => setFilter('city', e.target.value)}
              placeholder="e.g. Dubai"
            />

            <Select
              label="Source"
              placeholder="Any source"
              value={filters.source}
              onChange={(e) => setFilter('source', e.target.value)}
              options={(sources || []).map((s) => ({ value: s, label: s }))}
            />

            <Select
              label="Status"
              placeholder="Any status"
              value={filters.status}
              onChange={(e) => setFilter('status', e.target.value)}
              options={STATUS_OPTIONS}
            />

            <Select
              label="Pipeline stage"
              placeholder="Any stage"
              value={filters.stage}
              onChange={(e) => setFilter('stage', e.target.value)}
              options={(stages || []).map((s) => ({ value: s.key, label: s.name }))}
            />

            <Select
              label="Priority"
              placeholder="Any priority"
              value={filters.priority}
              onChange={(e) => setFilter('priority', e.target.value)}
              options={PRIORITY_OPTIONS}
            />

            <Select
              label="Assignment"
              placeholder="Assigned or not"
              value={filters.assignment}
              onChange={(e) => setFilter('assignment', e.target.value)}
              options={ASSIGNMENT_OPTIONS}
            />

            <Input
              label="Min lead score (0-100)"
              type="number"
              min="0"
              max="100"
              value={filters.minLeadScore}
              onChange={(e) => setFilter('minLeadScore', e.target.value)}
              placeholder="e.g. 50"
            />

            <Input
              label="Max lead score (0-100)"
              type="number"
              min="0"
              max="100"
              value={filters.maxLeadScore}
              onChange={(e) => setFilter('maxLeadScore', e.target.value)}
              placeholder="e.g. 100"
            />

            <Input
              label="Tags (comma separated)"
              type="text"
              value={filters.tags}
              onChange={(e) => setFilter('tags', e.target.value)}
              placeholder="e.g. whatsapp, referral"
            />

            <Input
              label="Contacted in last (days)"
              type="number"
              min="0"
              value={filters.lastContactedWithinDays}
              onChange={(e) => setFilter('lastContactedWithinDays', e.target.value)}
              placeholder="e.g. 7"
            />

            <Input
              label="Follow-up due within (days)"
              type="number"
              min="0"
              value={filters.nextFollowUpWithinDays}
              onChange={(e) => setFilter('nextFollowUpWithinDays', e.target.value)}
              placeholder="e.g. 3"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <label className="flex items-center gap-2 text-sm font-medium text-secondary-700 sm:col-span-1">
              <input
                type="checkbox"
                checked={filters.neverContacted}
                onChange={(e) => setFilter('neverContacted', e.target.checked)}
                className="w-4 h-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
              />
              Never contacted
            </label>
            <Input
              label="Created after"
              type="date"
              value={filters.createdAfter}
              onChange={(e) => setFilter('createdAfter', e.target.value)}
            />
            <Input
              label="Created before"
              type="date"
              value={filters.createdBefore}
              onChange={(e) => setFilter('createdBefore', e.target.value)}
            />
          </div>

          {/* Preview */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-1">
            <Button
              variant="outline"
              size="sm"
              icon={<HiOutlineSparkles className="w-4 h-4" />}
              onClick={() => previewMutation.mutate()}
              loading={previewMutation.isPending}
            >
              Preview matches
            </Button>
            {preview && (
              <span className="text-sm font-semibold text-secondary-700">
                {preview.count} lead{preview.count === 1 ? '' : 's'} match
                {preview.sample.length > 0 && (
                  <span className="text-secondary-400 font-normal">
                    {' '}— e.g. {preview.sample.slice(0, 3).map((l) => l.name).join(', ')}
                  </span>
                )}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 sm:gap-3 justify-end pt-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            loading={saveMutation.isPending}
          >
            {isEdit ? 'Update Smart List' : 'Create Smart List'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default SmartListModal;